import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { dayMonthYearFormat } from "shared/lib/datetime";
import { PriceCalendarOptions } from "shared/lib/priceCalendar";

import Calendar from "@components/SearchForm/Calendar/Calendar";
import Modal from "@components/UI/Modal";

interface Props {
  date: string;
  onChange: (value: string) => void;
  typeSearch: string;
  disabledTillDate: string;
  disabledAfterDate?: string;
  testId: string;
  priceCalendar?: PriceCalendarOptions;
  rangeSelectionEnabled?: boolean;
  departureDate?: string;
  returnDate?: string;
  onDepartureChange?: (value: string) => void;
  onReturnChange?: (value: string) => void;
  onOpenCalendar?: () => void;
}

const EXPORT_HTML_PACKAGE = Boolean(process.env.NEXT_PUBLIC_EXPORT_PACKAGE);
const CALENDAR_INITIAL_FOCUS_SELECTOR =
  ".header-search-form-calendar-initial-focus";

function DatePicker(props: Props): React.ReactElement {
  const [showCalendar, setShowCalendar] = useState(false);
  const currentLanguage = useSelector(
    (state: AppState) => state.requestorConfig.currentLanguage
  );
  const defaultCountry = useSelector(
    (state: AppState) =>
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      ""
  );

  const {
    date,
    onChange,
    typeSearch,
    disabledTillDate,
    testId,
    disabledAfterDate,
    priceCalendar,
    rangeSelectionEnabled,
    departureDate,
    returnDate,
    onDepartureChange,
    onReturnChange,
    onOpenCalendar,
  } = props;

  const dateNoon = moment(new Date(`${date} 12:00`));
  const handleOpenCalendar = () => {
    if (onOpenCalendar) {
      onOpenCalendar();
      return;
    }

    setShowCalendar(!showCalendar);
  };

  return (
    <div className="date-picker-wrapper" id={testId}>
      <span role="button" onClick={handleOpenCalendar}>
        <span className="header-search-form-inner-field-value">
          {dateNoon.locale(currentLanguage).format("dd").toUpperCase()}
        </span>{" "}
        <span>|</span>{" "}
        <span className="header-search-form-inner-field-additional">
          {moment(date).format(dayMonthYearFormat(defaultCountry))}
        </span>
      </span>
      {!onOpenCalendar && (
        <>
          <Modal
            isActive={showCalendar}
            onClose={() => setShowCalendar(false)}
            initialFocusSelector={CALENDAR_INITIAL_FOCUS_SELECTOR}
            innerClassName="header-search-form-calendar-modal"
          >
            <Calendar
              close={() => setShowCalendar(false)}
              date={date}
              onChange={onChange}
              typeSearch={typeSearch}
              disabledTillDate={disabledTillDate}
              disabledAfterDate={disabledAfterDate}
              priceCalendar={priceCalendar}
              rangeSelectionEnabled={rangeSelectionEnabled}
              departureDate={departureDate}
              returnDate={returnDate}
              onDepartureChange={onDepartureChange}
              onReturnChange={onReturnChange}
            />
          </Modal>
          {EXPORT_HTML_PACKAGE && (
            <Calendar
              close={() => setShowCalendar(false)}
              date={date}
              onChange={onChange}
              typeSearch={typeSearch}
              disabledTillDate={disabledTillDate}
              priceCalendar={priceCalendar}
              rangeSelectionEnabled={rangeSelectionEnabled}
              departureDate={departureDate}
              returnDate={returnDate}
              onDepartureChange={onDepartureChange}
              onReturnChange={onReturnChange}
            />
          )}
        </>
      )}
    </div>
  );
}

export default DatePicker;
